import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  Milestone,
  MilestoneIcon,
  Milestones,
  MilestoneStatus,
  MilestoneTitle,
} from "@/shared/bsm/core/process/components/ui/process-page-overview";
import { useRltCalculationListQuery } from "@/services/backend/rlt/calculations/api/calculation-api";
import {
  DocumentIconCalculation,
  DocumentIconCertificate,
  DocumentIconConfirmation,
  DocumentIconOffer,
  DocumentIconOrder,
  DocumentIconWorkOrder,
} from "@/services/backend/documents/components/document-icon";
import { formatDate } from "@/shared/lib/utilities/date";
import { useRltOfferListQuery } from "@/services/backend/rlt/offers/api/offer-api";
import { useRltOrderListQuery } from "@/services/backend/rlt/orders/api/order-api";
import { useRltConfirmationListQuery } from "@/services/backend/rlt/confirmations/api/confirmation-api";
import { useRltWorkOrderListQuery } from "@/services/backend/rlt/work-orders/api/work-order-api";
import { useRltCertificateListQuery } from "@/services/backend/rlt/certificates/api/certificate-api";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { RefreshCw } from "lucide-react";
import { GSMProcessKind } from "@/services/backend/processes/types/process-kind";
import { DocumentState } from "@/services/backend/documents/types/state";

export function RltMilestones({ processId }: { processId: string }) {
  const {
    data: process,
    isLoading,
    error,
  } = useProcessesProcessShowQuery({ id: processId });

  return (
    <Card className="pb-0">
      <CardHeader>
        <CardTitle>{t("Meilensteine")}</CardTitle>
        <CardDescription>
          {t(
            "Die Übersicht ist aktuell noch mehr illustrativ. Es wird lediglich ausgewertet ob ein entsprechendes Dokument erstellt wurde. Eine konkretere Logik wird folgen.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RTKQueryErrorAlert error={error} />
        {isLoading && (
          <div className="flex items-center gap-2">
            <RefreshCw className="size-4 animate-spin" />
            <span>{t("Lade Meilensteine...")}</span>
          </div>
        )}
        {process && (
          <Milestones>
            {process.processKind === GSMProcessKind.Full && (
              <CalculationMilestone processId={processId!} />
            )}
            {process.processKind === GSMProcessKind.Full && (
              <OfferMilestone processId={processId!} />
            )}
            {process.processKind === GSMProcessKind.Full && (
              <OrderMilestone processId={processId!} />
            )}
            {process.processKind === GSMProcessKind.Full && (
              <ConfirmationMilestone processId={processId!} />
            )}
            <WorkOrderMilestone processId={processId!} />
            <CertificateMilestone processId={processId!} />
          </Milestones>
        )}
      </CardContent>
    </Card>
  );
}

function CalculationMilestone({ processId }: { processId: string }) {
  const { data } = useRltCalculationListQuery({
    process: { active: true, values: [processId!] },
  });

  const offer = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconCalculation}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Kalkulation erstellen")}</MilestoneTitle>
      {offer && (
        <MilestoneStatus>
          {`Die letzte Kalkulation wurde am ${formatDate(offer.stateSetAt ?? "")} erstellt.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function OfferMilestone({ processId }: { processId: string }) {
  const { data } = useRltOfferListQuery({
    process: { active: true, values: [processId!] },
    state: { active: true, values: [DocumentState.Immutable] },
  });

  const offer = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconOffer}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Angebot versenden")}</MilestoneTitle>
      {offer && (
        <MilestoneStatus>
          {`Das letzte Angebot wurde am ${formatDate(offer.stateSetAt ?? "")} von ${offer.stateSetBy} versendet.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function OrderMilestone({ processId }: { processId: string }) {
  const { data } = useRltOrderListQuery({
    process: { active: true, values: [processId!] },
  });

  const order = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconOrder}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Auftragseingang")}</MilestoneTitle>
      {order && (
        <MilestoneStatus>
          {`Die letzte Bestellung ging am ${formatDate(order.date ?? "")} ein.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function ConfirmationMilestone({ processId }: { processId: string }) {
  const { data } = useRltConfirmationListQuery({
    process: { active: true, values: [processId!] },
    state: { active: true, values: [DocumentState.Immutable] },
  });

  const confirmation = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconConfirmation}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Auftragsbestätigung versendet")}</MilestoneTitle>
      {confirmation && (
        <MilestoneStatus>
          {`Die letzte Bestätigung wurde am ${formatDate(confirmation.date ?? "")} von ${confirmation.stateSetBy} versendet.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function WorkOrderMilestone({ processId }: { processId: string }) {
  const { data } = useRltWorkOrderListQuery({
    process: { active: true, values: [processId!] },
    state: { active: true, values: [DocumentState.Immutable] },
  });

  const workOrder = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconWorkOrder}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Arbeitsschein versendet")}</MilestoneTitle>
      {workOrder && (
        <MilestoneStatus>
          {`Die letzter Arbeitsschein wurde am ${formatDate(workOrder.endDate ?? "")} von ${workOrder.stateSetBy} versendet.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function CertificateMilestone({ processId }: { processId: string }) {
  const { data } = useRltCertificateListQuery({
    process: { active: true, values: [processId!] },
  });

  const certificate = data?.items[data.items.length - 1];

  return (
    <Milestone>
      <MilestoneIcon
        icon={DocumentIconCertificate}
        done={(data?.items.length ?? 0) > 0}
      />
      <MilestoneTitle>{t("Zertifikat versendet")}</MilestoneTitle>
      {certificate && (
        <MilestoneStatus>
          {`Das letzte Zertifikat wurde am ${formatDate(certificate.issueDate ?? "")} erstellt.`}
        </MilestoneStatus>
      )}
    </Milestone>
  );
}
